import axios from 'axios';
import { setupAnalyticsInterceptor } from '../lib/analyticsInterceptor';
import { useAuthStore } from '../store/auth';
import type {
    Agency,
    AuthResponse,
    BillingStatus,
    Customer,
    CustomerPayment,
    CustomerPaymentSummary,
    CustomerSummary,
    DashboardSummary,
    EnvTax,
    ExpiryAlertsResponse,
    FitnessCertificate,
    Insurance,
    Invitation,
    InvitationsResponse,
    Licence,
    MonthlyChartResponse,
    Profile,
    ProfilesResponse,
    PUC,
    RoadTax,
    SystemSettings,
    Vehicle
} from '../types/index';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // Send cookies (refresh token) automatically
})

// Request interceptor — attach Bearer token from state
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor — handle 401 (refresh) and 402 (billing)
let isRefreshing = false
let refreshQueue: Array<(token: string) => void> = []

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config

    // 401 — try to refresh token
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true

      if (isRefreshing) {
        // Queue requests while refresh is in progress
        return new Promise((resolve) => {
          refreshQueue.push((token) => {
            original.headers.Authorization = `Bearer ${token}`
            resolve(api(original))
          })
        })
      }

      isRefreshing = true
      try {
        // Get refresh token from store (with fallback to body if cookie fails)
        const refreshToken = useAuthStore.getState().refreshToken
        
        // Refresh token cookie is sent automatically via withCredentials
        // Also send in body as fallback
        const body = refreshToken ? { refresh_token: refreshToken } : {}
        
        const { data } = await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1'}/auth/refresh`,
          body,
          { withCredentials: true },
        )
        
        // Update both tokens
        useAuthStore.getState().setTokens({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        })
        
        // Process queued requests with new token
        refreshQueue.forEach((cb) => cb(data.access_token))
        refreshQueue = []
        original.headers.Authorization = `Bearer ${data.access_token}`
        return api(original)
      } catch (err: any) {
        console.error('[API] Token refresh failed:', {
          status: err.response?.status,
          error: err.response?.data,
          message: err.message,
        })
        useAuthStore.getState().clear()
        window.location.href = '/login'
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    // 402 — subscription expired, redirect to billing
    if (error.response?.status === 402) {
      window.location.href = '/billing'
      return Promise.reject(error)
    }

    return Promise.reject(error)
  },
)

// Setup analytics interceptor to track all API calls
setupAnalyticsInterceptor(api)

// Auth
export const authAPI = {
  register: (data: {
    agency_name: string
    license_number: string
    full_name: string
    email: string
    password: string
    referral_code?: string
  }) => api.post<AuthResponse>('/auth/register', data),

  login: (email: string, password: string) =>
    api.post<AuthResponse>('/auth/login', { email, password }),

  logout: (refresh_token?: string) =>
    api.post('/auth/logout', refresh_token ? { refresh_token } : {}),

  verifyEmail: (token: string) =>
    api.post('/auth/verify-email', { token }),

  resendVerificationEmail: (email: string) =>
    api.post('/auth/resend-verification', { email }),

  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (token: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, new_password: newPassword }),

  changePassword: (oldPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword }),
}

// Agency
export const agencyAPI = {
  get: () => api.get<Agency>('/agency'),
  update: (data: Partial<Agency>) => api.put<Agency>('/agency', data),
  getStats: () => api.get('/agency/stats'),
}

// Dashboard
export const dashboardAPI = {
  getSummary: () => api.get<DashboardSummary>('/dashboard/summary'),
  getExpiryAlerts: (params?: { limit?: number; record_type?: string }) =>
    api.get<ExpiryAlertsResponse>('/dashboard/expiry-alerts', { params }),
  getMonthlyChart: () => api.get<MonthlyChartResponse>('/dashboard/monthly-chart'),
  getAgentSummary: () => api.get('/dashboard/agent-summary'),
}

// Profiles
export const profileAPI = {
  getMe: () => api.get<Profile>('/profiles/me'),
  updateMe: (data: { full_name?: string; avatar_url?: string }) =>
    api.patch<Profile>('/profiles/me', data),
  list: () => api.get<ProfilesResponse>('/profiles'),
  updateStatus: (id: string, is_active: boolean) =>
    api.patch(`/profiles/${id}/status`, { is_active }),
}

// Invitations
export const invitationAPI = {
  create: (data: { email: string; phone?: string }) =>
    api.post<Invitation>('/invitations', data),
  list: (status?: string) =>
    api.get<InvitationsResponse>('/invitations', { params: status ? { status } : {} }),
  revoke: (id: string) => api.delete(`/invitations/${id}`),
  validate: (token: string) =>
    api.get('/invitations/accept', { params: { token } }),
  accept: (data: { token: string; full_name: string; password: string }) =>
    api.post<AuthResponse>('/invitations/accept', data),
}

// Billing
export const billingAPI = {
  getStatus: () => api.get<BillingStatus>('/billing/status'),
  createOrder: () => api.post('/billing/create-order'),
  getPayments: (params?: { limit?: number; offset?: number }) =>
    api.get('/billing/payments', { params }),
}

// Super Admin
export const superAdminAPI = {
  getSettings: () => api.get<SystemSettings>('/superadmin/settings'),
  updateSettings: (data: { subscription_price_rupees?: number; trial_period_days?: number }) =>
    api.put<SystemSettings>('/superadmin/settings', data),
}

// Referral
export const referralAPI = {
  getCode: () => api.get('/referral/code'),
  getStats: () => api.get('/referral/stats'),
}

// Customers
export const customerAPI = {
  list: (params?: {
    q?: string
    limit?: number
    offset?: number
    active?: boolean
  }) => api.get<{ customers: Customer[]; total: number }>('/customers', { params }),

  get: (id: string) => api.get<Customer>(`/customers/${id}`),

  getSummary: (id: string) => api.get<CustomerSummary>(`/customers/${id}/summary`),

  create: (data: Partial<Customer>) => api.post<Customer>('/customers', data),

  update: (id: string, data: Partial<Customer>) =>
    api.put<Customer>(`/customers/${id}`, data),

  delete: (id: string) => api.delete(`/customers/${id}`),
}

// Vehicles
export const vehicleAPI = {
  listAll: (params?: {
    vehicle_type?: string
    fuel_type?: string
    customer_id?: string
    status?: string
    search?: string
    limit?: number
    offset?: number
  }) => api.get<{ vehicles: Vehicle[]; total: number }>('/vehicles', { params }),

  listForCustomer: (customerId: string) =>
    api.get<{ vehicles: Vehicle[]; total: number }>(
      `/customers/${customerId}/vehicles`,
    ),

  get: (id: string) => api.get<Vehicle>(`/vehicles/${id}`),

  create: (customerId: string, data: Partial<Vehicle>) =>
    api.post<Vehicle>(`/customers/${customerId}/vehicles`, data),

  update: (id: string, data: Partial<Vehicle>) =>
    api.put<Vehicle>(`/vehicles/${id}`, data),

  delete: (id: string) => api.delete(`/vehicles/${id}`),
}

// Licences
export const licenceAPI = {
  list: (params?: {
    status?: string
    limit?: number
    offset?: number
  }) =>
    api.get<{ licences: Licence[]; total: number }>('/licences', { params }),

  listForCustomer: (customerId: string) =>
    api.get<{ licences: Licence[]; total: number }>(
      `/customers/${customerId}/licences`,
    ),

  create: (customerId: string, data: Partial<Licence>) =>
    api.post<Licence>(`/customers/${customerId}/licences`, data),

  update: (id: string, data: Partial<Licence>) =>
    api.put<Licence>(`/licences/${id}`, data),

  delete: (id: string) => api.delete(`/licences/${id}`),
}

// Insurances
export const insuranceAPI = {
  list: (params?: {
    status?: string
    policy_type?: string
    limit?: number
    offset?: number
  }) =>
    api.get<{ insurances: Insurance[]; total: number }>('/insurances', {
      params,
    }),

  listForCustomer: (customerId: string) =>
    api.get<{ insurances: Insurance[]; total: number }>(
      `/customers/${customerId}/insurances`,
    ),

  create: (customerId: string, data: Partial<Insurance>) =>
    api.post<Insurance>(`/customers/${customerId}/insurances`, data),

  update: (id: string, data: Partial<Insurance>) =>
    api.put<Insurance>(`/insurances/${id}`, data),

  delete: (id: string) => api.delete(`/insurances/${id}`),
}

// PUCs
export const pucAPI = {
  list: (params?: {
    status?: string
    result?: string
    limit?: number
    offset?: number
  }) =>
    api.get<{ pucs: PUC[]; total: number }>('/pucs', { params }),

  listForCustomer: (customerId: string) =>
    api.get<{ pucs: PUC[]; total: number }>(
      `/customers/${customerId}/pucs`,
    ),

  create: (customerId: string, data: Partial<PUC>) =>
    api.post<PUC>(`/customers/${customerId}/pucs`, data),

  update: (id: string, data: Partial<PUC>) =>
    api.put<PUC>(`/pucs/${id}`, data),

  delete: (id: string) => api.delete(`/pucs/${id}`),
}

// Fitness
export const fitnessAPI = {
  list: (params?: {
    status?: string
    result?: string
    fitness_class?: string
    limit?: number
    offset?: number
  }) =>
    api.get<{ fitness: FitnessCertificate[]; total: number }>(
      '/fitness',
      { params },
    ),

  listForCustomer: (customerId: string) =>
    api.get<{ fitness: FitnessCertificate[]; total: number }>(
      `/customers/${customerId}/fitness`,
    ),

  create: (customerId: string, data: Partial<FitnessCertificate>) =>
    api.post<FitnessCertificate>(`/customers/${customerId}/fitness`, data),

  update: (id: string, data: Partial<FitnessCertificate>) =>
    api.put<FitnessCertificate>(`/fitness/${id}`, data),

  delete: (id: string) => api.delete(`/fitness/${id}`),
}

// Road Taxes
export const roadTaxAPI = {
  list: (params?: {
    status?: string
    tax_type?: string
    state?: string
    limit?: number
    offset?: number
  }) =>
    api.get<{ road_taxes: RoadTax[]; total: number }>('/road-taxes', {
      params,
    }),

  listForCustomer: (customerId: string) =>
    api.get<{ road_taxes: RoadTax[]; total: number }>(
      `/customers/${customerId}/road-taxes`,
    ),

  create: (customerId: string, data: Partial<RoadTax>) =>
    api.post<RoadTax>(`/customers/${customerId}/road-taxes`, data),

  update: (id: string, data: Partial<RoadTax>) =>
    api.put<RoadTax>(`/road-taxes/${id}`, data),

  delete: (id: string) => api.delete(`/road-taxes/${id}`),
}

// Environmental Taxes
export const envTaxAPI = {
  list: (params?: {
    status?: string
    tax_category?: string
    limit?: number
    offset?: number
  }) =>
    api.get<{ env_taxes: EnvTax[]; total: number }>('/env-taxes', { params }),

  listForCustomer: (customerId: string) =>
    api.get<{ env_taxes: EnvTax[]; total: number }>(
      `/customers/${customerId}/env-taxes`,
    ),

  create: (customerId: string, data: Partial<EnvTax>) =>
    api.post<EnvTax>(`/customers/${customerId}/env-taxes`, data),

  update: (id: string, data: Partial<EnvTax>) =>
    api.put<EnvTax>(`/env-taxes/${id}`, data),

  delete: (id: string) => api.delete(`/env-taxes/${id}`),
}

// Customer Payments
export const customerPaymentAPI = {
  create: (customerId: string, data: {
    total_bill_amount: number
    paid_amount: number
    notes?: string
    items: Array<{
      service_type: string
      service_description: string
      item_amount: number
      quantity: number
      unit_price: number
      vehicle_id?: string
      vehicle_id_ref?: string
      insurance_id?: string
      licence_id?: string
      puc_id?: string
      fitness_id?: string
      road_tax_id?: string
      env_tax_id?: string
    }>
    transactions?: Array<{
      amount: number
      payment_method: 'cash' | 'upi' | 'card' | 'bank_transfer' | 'cheque'
      transaction_reference?: string
      notes?: string
    }>
  }) => api.post<CustomerPayment>(`/customer-payments?customer_id=${customerId}`, data),

  listByAgency: (params?: { limit?: number; offset?: number }) =>
    api.get<{ data: CustomerPayment[]; limit: number; offset: number }>(
      '/customer-payments/agency',
      { params },
    ),

  listByCustomer: (customerId: string) =>
    api.get<CustomerPayment[]>(`/customer-payments/customer/${customerId}`),

  getByCustomer: (customerId: string, limit: number = 20, offset: number = 0) =>
    api.get<CustomerPayment[]>(`/customer-payments/customer/${customerId}`, { params: { limit, offset } }),

  getById: (paymentId: string) =>
    api.get<CustomerPayment>(`/customer-payments/${paymentId}`),

  addTransaction: (paymentId: string, customerId: string, data: {
    amount: number
    payment_method: 'cash' | 'upi' | 'card' | 'bank_transfer' | 'cheque'
    transaction_reference?: string
    notes?: string
  }) => api.post(`/customer-payments/${paymentId}/transactions?customer_id=${customerId}`, data),

  getSummary: (customerId: string) =>
    api.get<CustomerPaymentSummary>(`/customer-payments/summary/customer/${customerId}`),

  getAgencySummary: () =>
    api.get('/customer-payments/summary/agency'),
}

export default api
